import React, { useState, useEffect, useRef } from "react";
import { getCardDetailsWithDOB } from "../../api";
import { VisaLogo, WhiteLogo, CardBackgroundGiff } from "./../../Public/assets";

const CreditCard = ({
  cardNumber = "",
  cardHolderName = " ",
  className = "",
  showUnlockButton = true,
  onDateSubmit,
  customBackground = null,
  customTextColor = "text-white",
  isFrozen = false,
  onUnfreeze,
  isStatusUpdating = false,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  // Get current month and year (initial default values) - using useMemo to avoid re-creation
  const defaultMonth = React.useMemo(() => {
    const currentDate = new Date();
    return String(currentDate.getMonth() + 1).padStart(2, '0');
  }, []);

  const defaultYear = React.useMemo(() => {
    const currentDate = new Date();
    return String(currentDate.getFullYear());
  }, []);

  // Separate state for day, month, year
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  // Refs for auto-focus functionality
  const dayInputRef = useRef(null);
  const monthInputRef = useRef(null);
  const yearInputRef = useRef(null);

  const formatCardNumber = (number) => {
    const cleaned = number.replace(/\s/g, "");
    const lastFour = cleaned.slice(-4);
    return { masked: "xxxx xxxx xxxx", lastFour };
  };

  const handleFlip = () => {
    if (isFlipped) return;
    setIsFlipped(true);
  };

  const handleDateSubmit = async () => {
    if (dateOfBirth.length >= 10) {
      // Validate date before submitting
      const validationError = validateDate(dateOfBirth);
      if (validationError) {
        setError(validationError);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Convert DD-MM-YYYY to YYYY-MM-DD for API
        const parts = dateOfBirth.split('-');
        const apiDateFormat = `${parts[2]}-${parts[1]}-${parts[0]}`;

        const response = await getCardDetailsWithDOB(apiDateFormat);

        if (response?.status === 200 && response?.data) {
          const { token, airwallexCardUrl } = response.data;

          // Fix URL domain if needed (api-demo -> demo)
          let correctedUrl = airwallexCardUrl;
          if (airwallexCardUrl.includes("api-demo.airwallex.com")) {
            correctedUrl = airwallexCardUrl.replace(
              "api-demo.airwallex.com",
              "demo.airwallex.com"
            );
          } else if (airwallexCardUrl.includes("api.airwallex.com")) {
            correctedUrl = airwallexCardUrl.replace(
              "api.airwallex.com",
              "airwallex.com"
            );
          }

          const hash = {
            token: token,
            langKey: "en",
            rules: {
              ".details": {
                backgroundColor: "transparent",
                padding: "0",
                margin: "0",
              },
              ".details__row": {
                backgroundColor: "transparent",
                marginBottom: "0",
              },
              ".details__row--card-number": {
                display: "flex",
                backgroundColor: "transparent",
                marginBottom: "10px",
              },
              ".details__row--expiry-date": {
                backgroundColor: "transparent",
                marginBottom: "0",
                marginRight: "60px",
                textAlign: "left",
                display: "flex",
              },
              ".details__row--security-code": {
                backgroundColor: "transparent",
                marginBottom: "0",
                marginLeft: "0",
                textAlign: "right",
                marginTop: "-60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              },
              ".details__content": {
                backgroundColor: "transparent",
              },
              ".details__label": {
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: "11px",
                fontWeight: "500",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "1.5px",
              },
              ".details__value": {
                color: "#FFFFFF",
                fontWeight: "600",
                fontSize: "20px",
                letterSpacing: "2px",
                marginLeft: "0",
                paddingLeft: "0",
              },
              ".details__button": {
                marginTop: "30px",
                marginLeft: "10px",
                // display: "none",
                // fontSize: "11px",
                // padding: "4px 8px",
                // backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#FFFFFF",
                // border: "none",
                // borderRadius: "4px",
                // cursor: "pointer",
              },
              ".details__button:hover": {
                // cursor: "pointer",
                // backgroundColor: "transparent",
                // color: "transparent",
              },
            },
          };

          // URL encode the JSON string (NOT base64!)
          const hashURI = encodeURIComponent(JSON.stringify(hash));

          // Build final iframe URL
          const fullIframeUrl = `${correctedUrl}${hashURI}`;

          setIframeUrl(fullIframeUrl);
          setIsUnlocked(true);
          onDateSubmit && onDateSubmit(dateOfBirth);

          // Don't flip back yet - wait for iframe to load (handled in useEffect)
          // Keep isLoading true until iframe loads
        } else {
          setError("Failed to fetch card details. Please try again.");
          setIsLoading(false);
        }
      } catch (err) {
        setError(err?.message || "Failed to unlock card. Please try again.");
        setIsLoading(false);
      }
    }
  };

  const validateDate = (dateString) => {
    if (dateString.length < 10) {
      return null; // Don't validate incomplete dates
    }

    // Parse DD-MM-YYYY format
    const parts = dateString.split('-');
    if (parts.length !== 3) {
      return "Please enter a valid date";
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JavaScript
    const year = parseInt(parts[2], 10);

    const inputDate = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    // Check if date is valid (handles invalid dates like 31-02-2024)
    if (
      isNaN(inputDate.getTime()) ||
      inputDate.getDate() !== day ||
      inputDate.getMonth() !== month ||
      inputDate.getFullYear() !== year
    ) {
      return "Please enter a valid date";
    }

    // Check if date is not greater than today
    if (inputDate > today) {
      return "Date cannot be greater than today";
    }

    return null; // No error
  };

  // Update the combined dateOfBirth whenever day, month, or year changes
  useEffect(() => {
    if (day && month && year) {
      setDateOfBirth(`${day}-${month}-${year}`);
    } else if (day) {
      setDateOfBirth(`${day}-${month}-${year}`);
    } else {
      setDateOfBirth("");
    }
  }, [day, month, year]);

  const handleDayChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove all non-numeric characters
    if (value.length <= 2) {
      setDay(value);
      // Auto-focus to month field when 2 digits are entered
      if (value.length === 2) {
        monthInputRef.current?.focus();
      }
    }
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleDayKeyDown = (e) => {
    // If backspace is pressed and field is empty, do nothing (already at first field)
    if (e.key === 'Backspace' && day === '') {
      // Already at the first field, no previous field to go to
    }
  };

  const handleMonthChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove all non-numeric characters
    if (value.length <= 2) {
      setMonth(value);
      // Auto-focus to year field when 2 digits are entered
      if (value.length === 2) {
        yearInputRef.current?.focus();
      }
    }
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleMonthKeyDown = (e) => {
    // If backspace is pressed and field is empty, go back to day field
    if (e.key === 'Backspace' && month === '') {
      dayInputRef.current?.focus();
    }
  };

  const handleYearChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove all non-numeric characters
    if (value.length <= 4) {
      setYear(value);
    }
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleYearKeyDown = (e) => {
    // If backspace is pressed and field is empty, go back to month field
    if (e.key === 'Backspace' && year === '') {
      monthInputRef.current?.focus();
    }
  };

  const handleDateBlur = () => {
    const validationError = validateDate(dateOfBirth);
    if (validationError) {
      setError(validationError);
    }
  };

  const handleCancel = (e) => {
    e?.stopPropagation(); // Prevent event bubbling
    setDay("");
    setMonth("");
    setYear("");
    setDateOfBirth("");
    setError(null);
    setIsFlipped(false);
  };

  const handleCloseIframe = () => {
    setIframeUrl(null);
    setIsUnlocked(false);
    setIframeLoaded(false);
    setDay("");
    setMonth("");
    setYear("");
    setDateOfBirth("");
  };

  // Auto-focus day input when card flips to back
  useEffect(() => {
    if (isFlipped && !isLoading && !isFrozen && dayInputRef.current) {
      // Small delay to ensure the flip animation is complete
      setTimeout(() => {
        dayInputRef.current?.focus();
      }, 300);
    }
  }, [isFlipped, isLoading, isFrozen]);

  // Clear date inputs when card is frozen
  useEffect(() => {
    if (isFrozen) {
      setDay("");
      setMonth("");
      setYear("");
      setDateOfBirth("");
      setError(null);
    }
  }, [isFrozen]);

  // Listen for iframe postMessage events
  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event.origin.includes("airwallex.com")) {
        const { type, height, error: iframeError } = event.data;

        // Check for loaded event
        if (type && type.includes("loaded")) {
          setIframeLoaded(true);
          setIsLoading(false);

          // Flip back to front after iframe is loaded
          setTimeout(() => {
            setIsFlipped(false);
          }, 300);

          // Hide iframe after 60 seconds
          setTimeout(() => {
            setIframeUrl(null);
            setIsUnlocked(false);
            setIframeLoaded(false);
            setDay("");
            setMonth("");
            setYear("");
            setDateOfBirth("");
          }, 60000);
        } else if (type && type.includes("resize") && height > 0) {
          if (iframeRef.current) {
            iframeRef.current.style.height = `${height}px`;
          }
        } else if (type && type.includes("error")) {
          console.error("❌ Iframe error:", iframeError);
          setError(`Iframe error: ${iframeError || "Unknown error"}`);
          setIsLoading(false);
        }
      }
    };

    window.addEventListener("message", handleIframeMessage);

    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [defaultMonth, defaultYear]);

  const { masked, lastFour } = formatCardNumber(cardNumber);

  return (
    <div className={`w-full ${className}`}>
      {/* Card Wrapper with 3D flip */}
      <div
        className="relative w-full max-w-md aspect-[1.6/1] min-h-[200px]"
        style={{ perspective: "1000px" }}
      >
        <div
          className="relative w-full h-full transition-all duration-[600ms] ease-in-out"
          style={{
            transformStyle: "preserve-3d",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          {/* Card Front */}
          <div
            className="absolute w-full h-full"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              MozBackfaceVisibility: "hidden",
              msBackfaceVisibility: "hidden",
              cursor:'pointer',
              pointerEvents: isFlipped ? "none" : "auto",
              zIndex: isFlipped ? 1 : 2,
            }}
               onClick={handleFlip}
          >
            <div
              className={`relative w-full h-full rounded-2xl  p-6 overflow-hidden ${customTextColor}`}
              // style={{
              //   filter: "drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08))",
              //   ...(customBackground
              //     ? { background: customBackground }
              //     : { background: "linear-gradient(135deg, #2A5BAF 0%, #172F56 50%, #2960CD 100%)" }
              //   ),
              // }}
            >
              {/* Video/GIF Background */}
                 {!customBackground && (
                <img
                  src={CardBackgroundGiff}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover rounded-2xl"
                  style={{ zIndex: 0 }}
                />
              )}


              {/* Status Updating Loader Overlay */}
              {isStatusUpdating && (
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-20 flex items-center justify-center rounded-2xl">
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-white"></div>
                    <p className="font-geomanistRegular text-white text-sm">
                      Updating card status...
                    </p>
                  </div>
                </div>
              )}

              {/* Card Header */}
              <div className="flex justify-between items-start relative z-10">
                <img src={WhiteLogo} alt="" />

                {showUnlockButton && !isUnlocked && (
                  <button
                    onClick={handleFlip}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M12 17v0" />
                      <path d="M8 11V7a4 4 0 018 0v4" />
                    </svg>
                    <span className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]">
                      Tap to Unlock
                    </span>
                  </button>
                )}

                {showUnlockButton && isUnlocked && (
                  <button
                    onClick={handleCloseIframe}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M12 17a2 2 0 100-4 2 2 0 000 4z" />
                      <path d="M8 11V7a4 4 0 014-4" />
                    </svg>
                    <span className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]">
                      Lock Card
                    </span>
                  </button>
                )}
              </div>

              {/* Card Number - Show iframe when unlocked, otherwise show masked */}
              {isUnlocked && iframeUrl ? (
                <div className="absolute top-1/3 left-6 right-6 bottom-20 min-h-[120px] z-10">
                  <iframe
                    ref={iframeRef}
                    src={iframeUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                      minHeight: "120px",
                      border: "none",
                      backgroundColor: "transparent",
                    }}
                    title="Secure Card Details"
                    sandbox="allow-scripts allow-same-origin"
                  />
                </div>
              ) : (
                <div className="absolute top-1/3 left-6 right-6 z-10">
                  <div className="flex items-center gap-2">
                    <div className=" font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]">
                      {masked}
                    </div>
                    <span className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]">
                      {lastFour}
                    </span>
                  </div>
                </div>
              )}

              {/* Card Details Row - Always show when not unlocked */}
              {!isUnlocked && (
                <div className="absolute bottom-20 left-6 right-6 flex justify-between z-10">
                  <div>
                    <div className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%] text-white/70 mb-1">
                      Valid till
                    </div>
                    <div className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]">
                      XX/XX
                    </div>
                  </div>

                  <div>
                    <div className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%] text-white/70 mb-1">
                      CVV
                    </div>
                    <div className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]">
                      XXX
                    </div>
                  </div>
                </div>
              )}

              {/* Card Footer */}
              <div className="absolute bottom-4 md:bottom-6 left-6 right-6 flex justify-between items-end z-10">
                <div className="font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]">
                  {cardHolderName || "XXXX XXXX"}
                </div>
                {/* <img src={VisaLogo} alt="" /> */}
              </div>
            </div>
          </div>

          {/* Card Back */}
          <div
            className="absolute w-full h-full"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              MozBackfaceVisibility: "hidden",
              msBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              pointerEvents: isFlipped ? "auto" : "none",
              zIndex: isFlipped ? 2 : 1,
            }}
          >
            <div
              className="relative w-full h-full rounded-2xl shadow-2xl p-6 text-white flex flex-col items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, #2A5BAF 0%, #172F56 50%, #2960CD 100%)",
                filter: "drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08))",
              }}
            >
              {/* Close Icon - Hide when loading */}
              {!isLoading && (
                <button
                  onClick={handleCancel}
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors z-50 cursor-pointer"
                  type="button"
                >
                  <span className="text-2xl leading-none font-geomanistRegular">
                    ×
                  </span>
                </button>
              )}

              <div className="flex flex-col items-center justify-center gap-6 w-full max-w-sm px-4">
                {isLoading && !iframeLoaded ? (
                  <>
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                    <p className="font-geomanistRegular font-normal text-lg text-center">
                      Loading secure card details...
                    </p>
                  </>
                ) : isFrozen ? (
                  <>
                    <p className="font-geomanistRegular font-semibold text-2xl text-center">
                      Card is Frozen
                    </p>
                    <p className="font-geomanistRegular font-normal text-base leading-[140%] tracking-[0%] text-center text-white/80">
                      Your card is currently frozen and cannot be used for
                      transactions.
                    </p>
                    <button
                      onClick={onUnfreeze}
                      className="px-8 py-3 bg-blueish text-white rounded-lg hover:bg-blueish  transition-colors font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%]"
                    >
                      Unfreeze Card
                    </button>
                  </>
                ) : (
                  <>
                    <p className="font-geomanistRegular font-normal text-base leading-[140%] tracking-[0%] text-center">
                      Please enter your date of birth to view your card details.
                    </p>

                    <div className="w-full flex items-center justify-center">
                      <div className="flex items-center bg-white/20 border border-white/30 rounded-lg overflow-hidden">
                        <input
                          ref={dayInputRef}
                          type="text"
                          className="w-10 px-2 py-3 bg-transparent text-white font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%] focus:outline-none text-center"
                          value={day}
                          placeholder="DD"
                          onChange={handleDayChange}
                          onKeyDown={handleDayKeyDown}
                          onBlur={handleDateBlur}
                          maxLength={2}
                          disabled={isLoading}
                        />
                        <span className="text-white/70 font-geomanistRegular text-base">-</span>
                        <input
                          ref={monthInputRef}
                          type="text"
                          className="w-12 px-2 py-3 bg-transparent text-white font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%] focus:outline-none text-center"
                          value={month}
                          placeholder="MM"
                          onChange={handleMonthChange}
                          onKeyDown={handleMonthKeyDown}
                          onBlur={handleDateBlur}
                          maxLength={2}
                          disabled={isLoading}
                        />
                        <span className="text-white/70 font-geomanistRegular text-base">-</span>
                        <input
                          ref={yearInputRef}
                          type="text"
                          className="w-16 px-2 py-3 bg-transparent text-white font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%] focus:outline-none text-center"
                          value={year}
                          placeholder="YYYY"
                          onChange={handleYearChange}
                          onKeyDown={handleYearKeyDown}
                          onBlur={handleDateBlur}
                          maxLength={4}
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    {error && (
                      <p className="text-red-300 text-sm font-geomanistRegular text-center">
                        {error}
                      </p>
                    )}

                    <button
                      onClick={handleDateSubmit}
                      disabled={isLoading || dateOfBirth.length < 10}
                      className="px-8 py-3 bg-blueish text-white rounded-lg hover:bg-blueish transition-colors font-geomanistRegular font-normal text-base leading-[100%] tracking-[0%] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? "Loading..." : "Submit"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCard;
